/**
 * 기존 벽 구간 창 — 기존벽 도구로 변을 누르면 열림
 * 변 하나에 기존 벽(패널 없음) 구간을 여러 개: 변 시작(왼쪽/위 끝)에서 거리 mm로 입력, 기본은 변 전체
 */
import React, { useState } from 'react';
import { selectAllOnFocus, useDragOffset } from './dialogKit';

export interface EdgeInfo { key: string; lo: number; hi: number; horiz: boolean }
interface Seg { a: number; b: number } // 변 시작에서 거리

export function checkSegs(segs: Seg[], L: number): string | null {
  const s = [...segs].sort((p, q) => p.a - q.a);
  for (let i = 0; i < s.length; i++) {
    if (!(s[i].b > s[i].a)) return `${i + 1}번 구간: 끝이 시작보다 커야 함`;
    if (s[i].a < 0 || s[i].b > L) return `${i + 1}번 구간: 변 길이(${L}mm) 밖`;
    if (i > 0 && s[i].a < s[i - 1].b) return '구간끼리 겹침';
  }
  return null;
}

export const ExistingWallDialog: React.FC<{
  edge: EdgeInfo;
  current: { from: number; to: number }[];   // 세계 좌표
  onApply: (segs: { from: number; to: number }[]) => void;
  onClose: () => void;
}> = ({ edge, current, onApply, onClose }) => {
  const L = Math.round(edge.hi - edge.lo);
  const [segs, setSegs] = useState<Seg[]>(() => current.map(c => ({ a: Math.round(Math.min(c.from, c.to) - edge.lo), b: Math.round(Math.max(c.from, c.to) - edge.lo) })));
  const drag = useDragOffset();
  const err = checkSegs(segs, L);
  const start = edge.horiz ? '왼쪽' : '위';
  const set = (i: number, k: keyof Seg, v: string) => setSegs(segs.map((s, j) => (j === i ? { ...s, [k]: Number(v) || 0 } : s)));
  const apply = (list: Seg[]) => { if (!checkSegs(list, L)) onApply(list.map(s => ({ from: edge.lo + s.a, to: edge.lo + s.b }))); };
  const inp: React.CSSProperties = { width: 80, padding: '5px 8px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8, textAlign: 'right' };
  const btn = (bg: string, solid = false): React.CSSProperties => ({ padding: '6px 12px', fontSize: 13, borderRadius: 5, cursor: 'pointer',
    border: solid ? 'none' : `1px solid ${bg}`, background: solid ? bg : '#fff', color: solid ? '#fff' : bg, fontWeight: solid ? 'bold' : 'normal' });
  return (
    <div style={{ position: 'fixed', top: 90, right: 24, width: 380, zIndex: 1000, background: '#fff', borderRadius: 20, overflow: 'hidden',
      boxShadow: '0 12px 40px rgba(0,0,0,0.16)', transform: `translate(${drag.offset.x}px, ${drag.offset.y}px)` }}>
      <div onMouseDown={drag.onMouseDown} style={{ background: '#fff', color: '#191F28', padding: '16px 20px 8px', borderRadius: 0,
        cursor: 'move', userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <b style={{ fontSize: 14 }}>▨ 기존 벽 — {edge.horiz ? '가로' : '세로'} 변 {L}mm</b>
        <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#F2F4F6', color: '#4E5968', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>✕</button>
      </div>
      <div style={{ padding: 14, fontSize: 13 }}>
        <div style={{ color: '#666', marginBottom: 8 }}>기존 벽 구간에는 패널을 넣지 않습니다. 거리는 이 변의 <b>{start} 끝</b>에서 mm.</div>
        {segs.length === 0 && <div style={{ color: '#999', padding: '6px 0' }}>기존 벽 없음 — 이 변 전체에 패널</div>}
        {segs.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ width: 22, color: '#888' }}>{i + 1}</span>
            <input value={s.a} onFocus={selectAllOnFocus} onChange={e => set(i, 'a', e.target.value)} inputMode="numeric" style={inp} />
            <span>~</span>
            <input value={s.b} onFocus={selectAllOnFocus} onChange={e => set(i, 'b', e.target.value)} inputMode="numeric" style={inp} />
            <span style={{ color: '#888', fontSize: 12 }}>({s.b - s.a}mm)</span>
            <button onClick={() => setSegs(segs.filter((_, j) => j !== i))} style={{ ...btn('#F04452'), padding: '2px 8px', fontSize: 11, marginLeft: 'auto' }}>빼기</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          <button onClick={() => setSegs([{ a: 0, b: L }])} style={btn('#3182F6')}>변 전체</button>
          <button onClick={() => {
            const last = segs.reduce((m, s) => Math.max(m, s.b), 0);
            setSegs([...segs, { a: Math.min(last, L), b: Math.min(last + 1000, L) }]);
          }} style={btn('#3182F6')}>+ 구간 추가</button>
        </div>
        {err && <div style={{ color: '#F04452', marginTop: 8 }}>⚠ {err}</div>}
        <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>기존 벽 옆에 남는 패널 조각이 최소 크기보다 짧으면 위쪽 「확인 필요」에 표시됩니다.</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={() => { setSegs([]); onApply([]); }} style={{ ...btn('#757575'), marginRight: 'auto' }}>모두 지우기</button>
          <button onClick={onClose} style={btn('#757575')}>닫기</button>
          <button onClick={() => apply(segs)} disabled={!!err} style={{ ...btn('#3182F6', true), opacity: err ? 0.4 : 1 }}>적용</button>
        </div>
      </div>
    </div>
  );
};
